import { Children } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
// Reuse the ARE overlay stylesheet as the single source of truth so this
// overlay stays pixel-identical to the ARE modal (the design reference).
import '../AdvisorVoiceDialog.css';

/**
 * Speech-to-speech voice overlay.
 *
 * Visual design ported 1:1 from the ARE overlay (AdvisorVoiceDialog / `.av-*`
 * styles): a compact circular blob with two pulsing rings on a dark blurred
 * backdrop, a small close button top-right, status + hint text — so the
 * Assistant-Lion and Client-Notes voice modes look exactly like the ARE modal.
 * Presentational only; the parent owns the voice state machine and passes any
 * session content (deliverables, progress, chips) as children.
 *
 * @param {string} voiceState - 'idle'|'initializing'|'listening'|'thinking'|'speaking'
 * @param {number} audioLevel - 0-1 mic volume; scales the blob (like ARE)
 * @param {Function} onStop - close/stop the voice agent (top-right button)
 * @param {Function} onBargeIn - interrupt; fires on overlay click while thinking/speaking
 * @param {React.ReactNode} children - session content shown under the status text
 */
export default function VoiceOverlay({ voiceState, audioLevel = 0, onStop, onBargeIn, children }) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (voiceState === 'idle') return null;

  const interactive = voiceState === 'thinking' || voiceState === 'speaking';
  const kids = Children.toArray(children); // drops falsy entries → reliable emptiness check

  const statusText =
    voiceState === 'initializing' ? (t('voice-connecting') || 'Connecting...')
    : voiceState === 'listening' ? (t('voice-listening') || 'Listening – speak now')
    : voiceState === 'thinking' ? (t('voice-processing') || 'Processing...')
    : voiceState === 'speaking' ? (t('voice-speaking') || 'Speaking...')
    : '';

  return (
    <div
      className="av-overlay"
      // Opaque, fully covering backdrop (not the semi-transparent blurred ARE
      // one). Uses background.default — the app's solid base colour — because
      // background.paper is a translucent white overlay that would show through.
      style={{ background: theme.palette.background.default, backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
    >
      <IconButton
        onClick={(e) => { e.stopPropagation(); onStop(); }}
        size="small"
        className="av-close"
        aria-label="Close voice agent"
        sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary', bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
      >
        <CloseIcon />
      </IconButton>

      <div
        className="av-body"
        onClick={interactive ? onBargeIn : undefined}
        style={{ cursor: interactive ? 'pointer' : 'default' }}
      >
        <div className="av-blob-wrap">
          <div className={`av-blob av-${voiceState}`} style={{ transform: `scale(${1 + audioLevel * 0.3})` }}>
            <div className="av-blob-inner" />
            <div className="av-blob-ring r1" />
            <div className="av-blob-ring r2" />
          </div>
        </div>

        <div className="av-status">{statusText}</div>

        {voiceState === 'initializing' && (
          <div className="av-hint">{t('voice-setting-up') || 'Setting up microphone...'}</div>
        )}
        {interactive && (
          <div className="av-hint">{t('voice-tap-interrupt') || 'Tap to interrupt and speak again'}</div>
        )}

        {kids.length > 0 && (
          <Box
            sx={{
              width: '100%',
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              maxHeight: '34%',
              overflowY: 'auto',
            }}
          >
            {kids}
          </Box>
        )}
      </div>
    </div>
  );
}
